import React from 'react';
import { formatDate, getWeekStart } from '../../../utils/calendarHelpers';

/**
 * MonthView - Calendar month grid view
 */
const MonthView = ({
  currentDate,
  setCurrentDate,
  setCurrentWeekStart,
  setViewMode,
  monthAvailability,
  availability,
  setSelectedSlot,
  setSelectedAvailability,
  setContextMenu
}) => {
  const handleDateClick = (date) => {
    setCurrentDate(date);
    setCurrentWeekStart(getWeekStart(date));
    setViewMode('day');
  };

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPadding = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const renderCells = () => {
    const cells = [];
    
    // Add padding for days before the first of the month
    for (let i = 0; i < startPadding; i++) {
      cells.push(
        <div key={`pad-${i}`} className="aspect-square bg-gray-50 rounded"></div>
      );
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateStr = formatDate(date);
      const isToday = formatDate(new Date()) === dateStr;
      const status = monthAvailability[dateStr] || {};
      const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
      
      let bgColor = 'bg-gray-50 hover:bg-gray-100';
      let dotColor = '';
      
      if (!isPast) {
        if (status.hasAvailability && status.hasAppointments) {
          bgColor = 'bg-yellow-50 hover:bg-yellow-100';
          dotColor = 'bg-yellow-500';
        } else if (status.hasAvailability) {
          bgColor = 'bg-green-50 hover:bg-green-100';
          dotColor = 'bg-green-500';
        } else if (status.hasAppointments) {
          bgColor = 'bg-red-50 hover:bg-red-100';
          dotColor = 'bg-red-500';
        }
      } else {
        bgColor = 'bg-gray-100';
      }
      
      cells.push(
        <div
          key={day}
          onClick={() => !isPast && handleDateClick(date)}
          onContextMenu={(e) => {
            if (isPast) return;
            e.preventDefault();
            const hasAvail = status.hasAvailability;
            
            setSelectedSlot({
              date: dateStr,
              time: '09:00',
              day: date,
              hour: 9
            });
            
            // Find availability for this date to set selectedAvailability
            if (hasAvail) {
              const dateAvailability = availability.find(a => a.date === dateStr);
              if (dateAvailability) {
                setSelectedAvailability(dateAvailability);
              }
            } else {
              setSelectedAvailability(null);
            }
            
            setContextMenu({
              x: e.clientX,
              y: e.clientY,
              hasAvailability: hasAvail,
              isPastSlot: false,
              isBookable: true,
              isCustomerBooking: false
            });
          }}
          className={`aspect-square ${bgColor} rounded flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isToday ? 'ring-2 ring-yellow-500' : ''
          } ${isPast ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <span className={`text-sm font-medium ${isToday ? 'text-yellow-700' : isPast ? 'text-gray-400' : 'text-gray-700'}`}>
            {day}
          </span>
          {dotColor && (
            <span className={`w-2 h-2 ${dotColor} rounded-full mt-1`}></span>
          )}
        </div>
      );
    }
    
    return cells;
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden p-4">
      {/* Month Grid Header */}
      <div className="grid grid-cols-7 mb-2">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
          <div key={day} className="text-center text-sm font-semibold text-gray-600 py-2">
            {day}
          </div>
        ))}
      </div>
      
      {/* Month Grid */}
      <div className="grid grid-cols-7 gap-1">
        {renderCells()}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t">
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-green-500 rounded-full"></span>
          <span className="text-sm text-gray-600">Available</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-yellow-500 rounded-full"></span>
          <span className="text-sm text-gray-600">Partially Booked</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 bg-red-500 rounded-full"></span>
          <span className="text-sm text-gray-600">Booked</span>
        </div>
      </div>
    </div>
  );
};

export default MonthView;
